<template>
  <Teleport to="body">
    <div v-if="isMetaPressed" class="vue-component-overlay-root">
      <a
        v-for="badge in badges"
        :key="badge.key"
        class="vue-component-badge"
        :href="createVsCodeUrl(badge.sourceFile)"
        :style="{
          top: `${badge.top}px`,
          left: `${badge.left}px`,
        }"
        :title="badge.sourceFile"
        @click.prevent.stop="openInVsCode(badge.sourceFile)"
      >
        {{ badge.componentName }}
      </a>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

type VueComponentType = {
  name?: string;
  __name?: string;
  __file?: string;
};

type VueComponentInstance = {
  uid: number;
  type: VueComponentType;
};

type VueOwnedElement = HTMLElement & {
  __vueParentComponent?: VueComponentInstance;
};

type Badge = {
  key: number;
  componentName: string;
  sourceFile: string;
  top: number;
  left: number;
};

const badges = ref<Badge[]>([]);
const isMetaPressed = ref(false);

let updateTimer: number | undefined;

function getComponentName(type: VueComponentType, sourceFile: string) {
  const filename = sourceFile
    .split(/[\\/]/)
    .pop()
    ?.replace(/\.vue$/, "");
  return type.name ?? type.__name ?? filename ?? "AnonymousComponent";
}

function isVisible(rect: DOMRect) {
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
}

function updateBadges() {
  if (!isMetaPressed.value) return;

  const componentElements = new Map<number, VueOwnedElement[]>();

  for (const element of document.body.querySelectorAll<VueOwnedElement>("*")) {
    if (element.closest(".vue-component-overlay-root")) continue;

    const instance = element.__vueParentComponent;
    const sourceFile = instance?.type.__file;
    if (!instance || !sourceFile || sourceFile.includes("/modules/devTools/")) continue;

    const elements = componentElements.get(instance.uid) ?? [];
    elements.push(element);
    componentElements.set(instance.uid, elements);
  }

  const nextBadges: Badge[] = [];

  for (const [uid, elements] of componentElements) {
    const visibleRects = elements
      .map((element) => element.getBoundingClientRect())
      .filter(isVisible);
    if (visibleRects.length === 0) continue;

    const instance = elements[0].__vueParentComponent;
    const sourceFile = instance?.type.__file;
    if (!instance || !sourceFile) continue;

    nextBadges.push({
      key: uid,
      componentName: getComponentName(instance.type, sourceFile),
      sourceFile,
      top: Math.max(0, Math.min(...visibleRects.map((rect) => rect.top))),
      left: Math.max(0, Math.min(...visibleRects.map((rect) => rect.left))),
    });
  }

  badges.value = nextBadges;
}

function startShowingBadges() {
  if (isMetaPressed.value) return;

  isMetaPressed.value = true;
  updateBadges();
  updateTimer = window.setInterval(updateBadges, 250);
}

function stopShowingBadges() {
  isMetaPressed.value = false;
  badges.value = [];

  if (updateTimer != null) {
    window.clearInterval(updateTimer);
    updateTimer = undefined;
  }
}

function onKeyDown(event: KeyboardEvent) {
  if (event.metaKey && event.ctrlKey) startShowingBadges();
}

function onKeyUp(event: KeyboardEvent) {
  if (!event.metaKey || event.ctrlKey) stopShowingBadges();
}

function openInVsCode(sourceFile: string) {
  window.location.assign(createVsCodeUrl(sourceFile));
}

function createVsCodeUrl(sourceFile: string) {
  const normalizedPath = sourceFile.replace(/\\/g, "/");
  const encodedPath = encodeURI(normalizedPath).replaceAll("#", "%23").replaceAll("?", "%3F");
  return `vscode://file${encodedPath}:1`;
}

onMounted(() => {
  window.addEventListener("keydown", onKeyDown, true);
  window.addEventListener("keyup", onKeyUp, true);
  window.addEventListener("blur", stopShowingBadges);
});

onBeforeUnmount(() => {
  stopShowingBadges();
  window.removeEventListener("keydown", onKeyDown, true);
  window.removeEventListener("keyup", onKeyUp, true);
  window.removeEventListener("blur", stopShowingBadges);
});
</script>

<style scoped>
.vue-component-overlay-root {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 2147483647;
}

.vue-component-badge {
  position: absolute;
  pointer-events: auto;
  appearance: none;
  border: 0;
  border-radius: 3px;
  padding: 2px 5px;
  background: rgb(37 99 235 / 90%);
  box-shadow: 0 2px 8px rgb(0 0 0 / 20%);
  color: #fff;
  cursor: pointer;
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
  font-size: 10px;
  line-height: 1.2;
  text-decoration: none;
  white-space: nowrap;
}

.vue-component-badge:hover {
  background: rgb(29 78 216);
}
</style>
